import React, { useState } from 'react';
import Link from '../components/LocalizedLink';

import { ArrowRight, ArrowLeft, Check, Sparkles, Zap, Crown, Building2, HelpCircle, MessageCircle, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import PackageRequestModal from '../components/PackageRequestModal';

const TariffePage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    attivita: null,
    esigenze: null
  });
  const [showResult, setShowResult] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handlePackageSelect = (pacchettoKey) => {
    const pacchetto = pacchetti[pacchettoKey];
    setSelectedPackage({
      nome: pacchetto.nome,
      iniziale: pacchetto.unaTantum,
      mensile: pacchetto.mensile,
      dettagli: pacchetto.descrizione
    });
    setShowModal(true);
  };

  const questions = [
    {
      id: 'attivita',
      question: t('tariffe.q1_question'),
      options: [
        { value: 'ristorante_pizzeria_bar', label: t('tariffe.q1_opt_ristorante'), icon: '🍽️' },
        { value: 'hotel', label: t('tariffe.q1_opt_hotel'), icon: '🏨' },
        { value: 'negozio', label: t('tariffe.q1_opt_negozio'), icon: '🛍️' },
        { value: 'agenzia_servizi', label: t('tariffe.q1_opt_agenzia'), icon: '💼' }
      ]
    },
    {
      id: 'esigenze',
      question: t('tariffe.q2_question'),
      subtitle: t('tariffe.q2_subtitle'),
      options: [
        { value: 'lite', label: t('tariffe.q2_opt_lite_label'), desc: t('tariffe.q2_opt_lite_desc'), icon: '🌐' },
        { value: 'standard', label: t('tariffe.q2_opt_standard_label'), desc: t('tariffe.q2_opt_standard_desc'), icon: '⚡' },
        { value: 'pro', label: t('tariffe.q2_opt_pro_label'), desc: t('tariffe.q2_opt_pro_desc'), icon: '🚀' }
      ]
    }
  ];

  const scontoLancio = 0.20;
  const applicaSconto = (prezzo) => Math.round(prezzo * (1 - scontoLancio));

  const pacchetti = {
    lite: {
      nome: t('tariffe.packages.lite.nome'),
      icon: Sparkles,
      colore: 'var(--color-accent)',
      coloreBg: 'rgba(245, 158, 11, 0.08)',
      unaTantumOriginale: 15,
      unaTantum: applicaSconto(15),
      mensile: 19.99,
      prezzoVariabile: true,
      descrizione: t('tariffe.packages.lite.descrizione'),
      tipoProgetto: t('tariffe.packages.lite.tipoProgetto'),
      incluso: [
        t('tariffe.packages.lite.incluso_1'),
        t('tariffe.packages.lite.incluso_2'),
        t('tariffe.packages.lite.incluso_3'),
        t('tariffe.packages.lite.incluso_4')
      ],
      esempiUso: t('tariffe.packages.lite.esempiUso'),
      inclusoMensile: [
        t('tariffe.packages.lite.mensile_1'),
        t('tariffe.packages.lite.mensile_2')
      ],
      dettagliAssistenza: 'base'
    },
    standard: {
      nome: t('tariffe.packages.standard.nome'),
      icon: Zap,
      colore: 'var(--color-primary)',
      coloreBg: 'rgba(16, 185, 129, 0.08)',
      unaTantumOriginale: 45,
      unaTantum: applicaSconto(45),
      mensile: 35,
      prezzoVariabile: true,
      descrizione: t('tariffe.packages.standard.descrizione'),
      tipoProgetto: t('tariffe.packages.standard.tipoProgetto'),
      consigliato: true,
      incluso: [
        t('tariffe.packages.standard.incluso_1'),
        t('tariffe.packages.standard.incluso_2'),
        t('tariffe.packages.standard.incluso_3'),
        t('tariffe.packages.standard.incluso_4')
      ],
      esempiUso: t('tariffe.packages.standard.esempiUso'),
      inclusoMensile: [
        t('tariffe.packages.standard.mensile_1'),
        t('tariffe.packages.standard.mensile_2'),
        t('tariffe.packages.standard.mensile_3')
      ],
      dettagliAssistenza: 'base'
    },
    pro: {
      nome: t('tariffe.packages.pro.nome'),
      icon: Crown,
      colore: '#8B5CF6',
      coloreBg: 'rgba(139, 92, 246, 0.08)',
      unaTantumOriginale: 150,
      unaTantum: applicaSconto(150),
      mensile: 85,
      aPartireDa: true,
      prezzoVariabile: true,
      descrizione: t('tariffe.packages.pro.descrizione'),
      tipoProgetto: t('tariffe.packages.pro.tipoProgetto'),
      incluso: [
        t('tariffe.packages.pro.incluso_1'),
        t('tariffe.packages.pro.incluso_2'),
        t('tariffe.packages.pro.incluso_3'),
        t('tariffe.packages.pro.incluso_4')
      ],
      esempiUso: t('tariffe.packages.pro.esempiUso'),
      inclusoMensile: [
        t('tariffe.packages.pro.mensile_1'),
        t('tariffe.packages.pro.mensile_2'),
        t('tariffe.packages.pro.mensile_3')
      ],
      dettagliAssistenza: 'pro'
    },
    enterprise: {
      nome: t('tariffe.packages.enterprise.nome'),
      icon: Building2,
      colore: '#1e40af',
      coloreBg: 'rgba(30, 64, 175, 0.08)',
      unaTantumOriginale: 500,
      unaTantum: applicaSconto(500),
      mensile: 199,
      aPartireDa: true,
      prezzoVariabile: true,
      descrizione: t('tariffe.packages.enterprise.descrizione'),
      tipoProgetto: t('tariffe.packages.enterprise.tipoProgetto'),
      incluso: [
        t('tariffe.packages.enterprise.incluso_1'),
        t('tariffe.packages.enterprise.incluso_2'),
        t('tariffe.packages.enterprise.incluso_3'),
        t('tariffe.packages.enterprise.incluso_4'),
        t('tariffe.packages.enterprise.incluso_5')
      ],
      esempiUso: t('tariffe.packages.enterprise.esempiUso'),
      inclusoMensile: [
        t('tariffe.packages.enterprise.mensile_1'),
        t('tariffe.packages.enterprise.mensile_2'),
        t('tariffe.packages.enterprise.mensile_3'),
        t('tariffe.packages.enterprise.mensile_4')
      ],
      dettagliAssistenza: 'enterprise'
    }
  };

  const assistenzaInfo = {
    base: [
      t('tariffe.assistenza.base_1'),
      t('tariffe.assistenza.base_2'),
      t('tariffe.assistenza.base_3'),
      t('tariffe.assistenza.base_4')
    ],
    pro: [
      t('tariffe.assistenza.pro_1'),
      t('tariffe.assistenza.pro_2'),
      t('tariffe.assistenza.pro_3'),
      t('tariffe.assistenza.pro_4'),
      t('tariffe.assistenza.pro_5')
    ],
    enterprise: [
      t('tariffe.assistenza.enterprise_1'),
      t('tariffe.assistenza.enterprise_2'),
      t('tariffe.assistenza.enterprise_3'),
      t('tariffe.assistenza.enterprise_4'),
      t('tariffe.assistenza.enterprise_5')
    ]
  };

  const handleSelect = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const canProceed = () => {
    const q = questions[step];
    return answers[q.id] !== null;
  };

  const nextStep = () => {
    if (step < questions.length - 1) setStep(step + 1);
    else setShowResult(true);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setStep(0);
    setAnswers({ attivita: null, esigenze: null });
    setShowResult(false);
  };

  const getPacchettoConsigliato = () => {
    return pacchetti[answers.esigenze] || pacchetti.standard;
  };

  // Wizard
  if (!showResult) {
    const currentQ = questions[step];
    
    return (
      <div data-testid="tariffe-page" className="min-h-screen bg-[var(--color-bg)]">
        <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
          {/* Progress */}
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm text-[var(--color-text-muted)]">
              {t('tariffe.step_of', { current: step + 1, total: questions.length })}
            </span>
            <div className="flex gap-2">
              {questions.map((_, i) => (
                <motion.div 
                  key={i}
                  className={`w-12 h-1.5 rounded-full ${
                    i <= step ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'
                  }`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                />
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] mb-2">
                {currentQ.question}
              </h1>
              {currentQ.subtitle && (
                <p className="text-[var(--color-text-muted)] mb-8">{currentQ.subtitle}</p>
              )}
              {!currentQ.subtitle && <div className="mb-8" />}

              <div className="space-y-3 mb-6">
                {currentQ.options.map((option, idx) => {
                  const isSelected = answers[currentQ.id] === option.value;
                  
                  return (
                    <motion.button
                      key={option.value}
                      onClick={() => handleSelect(currentQ.id, option.value)}
                      className={`w-full p-5 rounded-2xl border-2 text-left transition-colors ${
                        isSelected
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                          : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)]/40'
                      }`}
                      data-testid={`option-${option.value}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center gap-4">
                        {option.icon && <span className="text-2xl">{option.icon}</span>}
                        <div className="flex-1">
                          <span className={`font-medium text-lg block ${isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                            {option.label}
                          </span>
                          {option.desc && (
                            <span className="text-sm text-[var(--color-text-muted)]">{option.desc}</span>
                          )}
                        </div>
                        {isSelected && (
                          <motion.div 
                            className="w-6 h-6 rounded-full bg-[var(--color-primary)] flex items-center justify-center"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500 }}
                          >
                            <Check className="w-4 h-4 text-white" />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mb-8 text-center">
            <Link 
              to="/contatti" 
              className="inline-flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              data-testid="direct-quote-link"
            >
              <HelpCircle className="w-4 h-4" />
              {t('tariffe.direct_quote')}
            </Link>
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={step === 0}
              className="text-[var(--color-text-muted)]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.indietro')}
            </Button>
            
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className="btn-primary"
              data-testid="next-btn"
            >
              {step === questions.length - 1 ? t('tariffe.discover_package') : t('common.avanti')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const pacchettoScelto = getPacchettoConsigliato();

  return (
    <div data-testid="tariffe-result" className="min-h-screen bg-[var(--color-bg)]">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="label-tag mb-2 inline-block">{t('tariffe.result_tag')}</span>
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] mb-4">
            {t('tariffe.result_title', { name: pacchettoScelto.nome })}
          </h1>
          <p className="text-[var(--color-text-muted)] max-w-xl mx-auto">
            {pacchettoScelto.descrizione}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {Object.entries(pacchetti).map(([key, pacchetto], index) => {
            const Icona = pacchetto.icon;
            const isConsigliato = key === answers.esigenze;
            
            return (
              <motion.div 
                key={key}
                className={`card-elevated p-6 relative ${isConsigliato ? 'border-2 border-[var(--color-primary)] ring-4 ring-[var(--color-primary)]/10' : ''}`}
                data-testid={`pacchetto-${key}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                {isConsigliato && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--color-primary)] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    {t('tariffe.recommended_badge')}
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${pacchetto.colore}15` }}
                  >
                    <Icona className="w-5 h-5" style={{ color: pacchetto.colore }} />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text)]">{pacchetto.nome}</h2>
                </div>

                <div className="mb-6 pb-6 border-b border-[var(--color-border)]">
                  <div className="inline-block bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                    {t('tariffe.launch_discount')}
                  </div>
                  
                  {pacchetto.aPartireDa && (
                    <p className="text-xs text-[var(--color-text-muted)] mb-1">{t('tariffe.starting_from')}</p>
                  )}
                  <div className="flex items-baseline gap-1.5 flex-wrap mb-1">
                    <p className="text-2xl font-bold text-[var(--color-text)]">
                      {pacchetto.unaTantum}€
                    </p>
                    <p className="text-sm text-[var(--color-text-muted)] line-through">
                      {pacchetto.unaTantumOriginale}€
                    </p>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs text-[var(--color-text-muted)]">+</span>
                    <p className="text-xl font-bold" style={{ color: pacchetto.colore }}>
                      {pacchetto.mensile}€
                    </p>
                    <span className="text-sm text-[var(--color-text-muted)]">{t('tariffe.per_month')}</span>
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)] mt-2">
                    {t('tariffe.variable_price')}
                  </p>
                </div>

                {pacchetto.tipoProgetto && (
                  <div className="mb-4 p-3 rounded-xl" style={{ backgroundColor: pacchetto.coloreBg }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: pacchetto.colore }}>
                      {pacchetto.tipoProgetto}
                    </p>
                    <p className="text-sm text-[var(--color-text)]">
                      {pacchetto.descrizione}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-2">{t('tariffe.what_includes')}</p>
                  <ul className="space-y-2">
                    {pacchetto.incluso.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text)]">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {pacchetto.esempiUso && (
                    <p className="mt-3 text-xs text-[var(--color-primary)] bg-[var(--color-primary)]/5 p-2 rounded-lg">
                      💡 {pacchetto.esempiUso}
                    </p>
                  )}
                </div>
                <div className="mb-6">
                  <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide mb-2">{t('tariffe.in_monthly')}</p>
                  <ul className="space-y-2">
                    {pacchetto.inclusoMensile.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-text)]">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: pacchetto.colore }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-3 p-3 bg-[var(--color-bg)] rounded-lg">
                    <p className="text-xs font-semibold text-[var(--color-text-muted)] mb-2">
                      {pacchetto.dettagliAssistenza === 'enterprise' ? t('tariffe.assistance_enterprise') : 
                       pacchetto.dettagliAssistenza === 'pro' ? t('tariffe.assistance_pro') : t('tariffe.assistance_base')}
                    </p>
                    <ul className="space-y-1">
                      {assistenzaInfo[pacchetto.dettagliAssistenza].slice(0, 3).map((item, i) => (
                        <li key={i} className="text-xs text-[var(--color-text-muted)] flex items-start gap-1.5">
                          <span className="text-emerald-500">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Button 
                  className={`w-full rounded-full h-11 ${isConsigliato ? 'btn-primary' : ''}`}
                  variant={isConsigliato ? 'default' : 'outline'}
                  onClick={() => handlePackageSelect(key)}
                  data-testid={`select-${key}-btn`}
                >
                  {t('tariffe.request_package', { name: pacchetto.nome })}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link 
            to="/tariffe/listino" 
            className="inline-flex items-center gap-2 text-[var(--color-primary)] hover:underline"
          >
            <HelpCircle className="w-4 h-4" />
            {t('tariffe.compare_link')}
          </Link>
        </motion.div>

        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Link 
            to="/tariffe/configuratore" 
            className="inline-flex items-center gap-2 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
            data-testid="configuratore-link"
          >
            <Settings className="w-3 h-3" />
            {t('tariffe.custom_link')}
          </Link>
        </motion.div>

        <motion.div 
          className="highlight-box p-6 text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-[var(--color-text)] mb-4">
            {t('tariffe.whatsapp_pitch')}
          </p>
          <a href="https://wa.me/393534992310" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-full border-2 border-[var(--color-accent)] text-[var(--color-accent)]">
              <MessageCircle className="w-5 h-5 mr-2" />
              {t('tariffe.whatsapp_btn')}
            </Button>
          </a>
        </motion.div>

        <div className="flex flex-col items-center gap-4">
          <Button
            variant="ghost"
            onClick={restart}
            className="text-[var(--color-text-muted)]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.ricomincia')}
          </Button>
        </div>

        <p className="text-xs text-center text-[var(--color-text-muted)] mt-8 max-w-md mx-auto">
          {t('tariffe.disclaimer')}
        </p>
      </div>

      <PackageRequestModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        packageInfo={selectedPackage || { nome: '', iniziale: 0, mensile: 0 }}
      />
    </div>
  );
};

export default TariffePage;
